import { useEffect, useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, User, Send, CheckCircle2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, Eyebrow } from "@/components/motion";
import { setPageMeta } from "@/components/seo";
import { BUSINESS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// On the Emergent preview the form posts to the FastAPI backend; on static
// hosting (e.g. GoDaddy cPanel) it posts to the same-origin PHP handler.
const shouldUsePhp = () => {
  try {
    return new URL(process.env.REACT_APP_BACKEND_URL).origin !== window.location.origin;
  } catch {
    return true;
  }
};

const INFO = [
  { icon: User, label: "Contact", value: `${BUSINESS.owner} — ${BUSINESS.name}`, href: null, testid: "contact-owner" },
  { icon: Phone, label: "Phone", value: BUSINESS.phone, href: BUSINESS.phoneHref, testid: "contact-phone" },
  { icon: Mail, label: "Email", value: BUSINESS.email, href: BUSINESS.emailHref, testid: "contact-email" },
  { icon: MapPin, label: "Service Area", value: "Northern Rivers NSW", href: null, testid: "contact-area" },
];

const EMPTY = { full_name: "", business_name: "", email: "", phone: "", customer_type: "Residential", enquiry: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    setPageMeta(
      "Contact | LAD CO Electrical — Electrician Northern Rivers NSW",
      "Contact LAD CO Electrical — your local Level 2 accredited electrician in the Northern Rivers NSW. Call 0407 202 965 for residential, commercial and Level 2 electrical services."
    );
  }, []);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      if (shouldUsePhp()) {
        const data = new FormData();
        Object.entries(form).forEach(([k, v]) => data.append(k, v));
        await axios.post("/api/contact.php", data);
      } else {
        await axios.post(`${API}/contact`, form);
      }
      setStatus("sent");
    } catch (err) {
      setStatus("idle");
      setError("Something went wrong sending your enquiry. Please try again or call us directly.");
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Contact LAD CO Electrical"
        sub="Fast, friendly and experienced electrical services across the Northern Rivers region of NSW."
      />

      <section className="bg-white" data-testid="contact-section">
        <div className="container-site py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2 space-y-px bg-gray-200 border border-gray-200 h-fit">
            {INFO.map((item, i) => (
              <Reveal key={item.label} delay={i * 0.06} className="bg-white">
                <div className="p-6 flex items-start gap-4">
                  <span className="w-11 h-11 bg-ink flex items-center justify-center shrink-0">
                    <item.icon className="w-4.5 h-4.5 w-5 h-5 text-volt-bright" />
                  </span>
                  <div className="min-w-0">
                    <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500">{item.label}</span>
                    {item.href ? (
                      <a href={item.href} className="block font-heading font-bold text-ink mt-1.5 hover:text-volt transition-colors duration-200 break-all" data-testid={item.testid}>
                        {item.value}
                      </a>
                    ) : (
                      <span className="block font-heading font-bold text-ink mt-1.5" data-testid={item.testid}>{item.value}</span>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1} className="lg:col-span-3">
            {status === "sent" ? (
              <div className="border border-volt/40 bg-volt/5 p-10 text-center h-full flex flex-col items-center justify-center" data-testid="contact-success">
                <CheckCircle2 className="w-12 h-12 text-volt" />
                <h2 className="font-heading text-2xl font-bold text-ink mt-6">Thanks! Your enquiry has been received.</h2>
                <p className="text-gray-600 mt-3">The LAD CO Electrical team will be in touch soon.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-gray-200 p-8 lg:p-10" data-testid="contact-form">
                <Eyebrow>Send an Enquiry</Eyebrow>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6">
                  <div>
                    <label htmlFor="c-name" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2">Full Name *</label>
                    <input id="c-name" required value={form.full_name} onChange={set("full_name")} className="input-field" data-testid="contact-name-input" />
                  </div>
                  <div>
                    <label htmlFor="c-business" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2">Business Name (optional)</label>
                    <input id="c-business" value={form.business_name} onChange={set("business_name")} className="input-field" data-testid="contact-business-input" />
                  </div>
                  <div>
                    <label htmlFor="c-email" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2">Email *</label>
                    <input id="c-email" type="email" required value={form.email} onChange={set("email")} className="input-field" data-testid="contact-email-input" />
                  </div>
                  <div>
                    <label htmlFor="c-phone" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2">Phone *</label>
                    <input id="c-phone" type="tel" required value={form.phone} onChange={set("phone")} className="input-field" data-testid="contact-phone-input" />
                  </div>
                  <div>
                    <label htmlFor="c-type" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2">Customer Type *</label>
                    <select id="c-type" value={form.customer_type} onChange={set("customer_type")} className="input-field" data-testid="contact-customer-type">
                      <option>Residential</option>
                      <option>Commercial</option>
                      <option>Industrial</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="c-enquiry" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2">Enquiry *</label>
                    <input id="c-enquiry" required value={form.enquiry} onChange={set("enquiry")} className="input-field" placeholder="What is your enquiry about?" data-testid="contact-enquiry-input" />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="c-message" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2">Message *</label>
                    <textarea id="c-message" required rows={5} value={form.message} onChange={set("message")} className="input-field resize-none" data-testid="contact-message-input" />
                  </div>
                </div>
                {error && <p className="text-sm text-volt-dark font-semibold mt-4" data-testid="contact-error">{error}</p>}
                <button type="submit" disabled={status === "sending"} className="btn-volt mt-8 disabled:opacity-60" data-testid="contact-form-submit-button">
                  {status === "sending" ? "Sending..." : <>Send Enquiry <Send className="w-4 h-4" /></>}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Contact;
